import React, { useRef, useEffect, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import OnboardingSidebarChat from '@/components/onboarding/OnboardingSidebarChat';
import MobileAnalysisProgress from '@/components/onboarding/mobile/MobileAnalysisProgress';
import CalendarDrawer from '@/components/CalendarDrawer';
import WritePostDrawer from '@/components/WritePostDrawer';
import AppDrawer from '@/components/ui/AppDrawer';
import IdeaCards from '@/components/chat/IdeaCards';
import PostDraftCard from '@/components/chat/PostDraftCard';
import SignupDialog from '@/components/dialogs/SignupDialog';
import ReactMarkdown from 'react-markdown';
import { Send, Plus, Mic } from 'lucide-react';
import type { OnboardingStep } from '@/types/onbaording.types';
import type { CalendarItem } from '@/components/CalendarDrawer';
import type { PostIdea } from '@/components/chat/IdeaCards';
import { addToCalendar } from '@/utils/duplicatePrevention';
import { supabase } from '@/integrations/supabase/client';
import { useAppState } from '@/context/AppStateContext';
import { useNavigate } from 'react-router-dom';

type ChatMessage = {
  role: 'assistant' | 'user';
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
};

interface OnboardingChatDesktopProps {
  messages: ChatMessage[];
  input: string;
  setInput: (val: string) => void;
  sending: boolean;
  onSend: () => void;
  onSignupClick: () => void;
  displaySteps: OnboardingStep[];
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  calendarItems: CalendarItem[];
  setCalendarItems: React.Dispatch<React.SetStateAction<CalendarItem[]>>;
  writeDrawerOpen: boolean;
  setWriteDrawerOpen: (open: boolean) => void;
  writeDrawerData: { id?: string; subject_matter: string; post_content: string; post_digest?: string } | null;
  setWriteDrawerData: React.Dispatch<React.SetStateAction<{ id?: string; subject_matter: string; post_content: string; post_digest?: string } | null>>;
  handleCreateOutline: (idea: PostIdea) => void;
  generateDigest: (content: string, maxLength?: number) => string;
  scrollRef: React.RefObject<HTMLDivElement>;
  currentWebsite?: { id: string; url: string };
}

export default function OnboardingChatDesktop({
  messages,
  input,
  setInput,
  sending,
  onSend,
  onSignupClick,
  displaySteps,
  drawerOpen,
  setDrawerOpen,
  calendarItems,
  setCalendarItems,
  writeDrawerOpen,
  setWriteDrawerOpen,
  writeDrawerData,
  setWriteDrawerData,
  handleCreateOutline,
  generateDigest,
  scrollRef,
  currentWebsite,
}: OnboardingChatDesktopProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<{ email: string; provider?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        const provider = data.session.user.app_metadata.provider || 'email';
        setUser({ 
          email: data.session.user.email || 'User',
          provider
        });
      }
      setLoading(false);
    };
    
    getUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          const provider = session.user.app_metadata.provider || 'email';
          setUser({ 
            email: session.user.email || 'User',
            provider
          });
        } else {
          setUser(null);
        }
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  // Focus input after sending message
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);
  
  return (
    <div 
      className="flex h-screen w-full gap-6 relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.9)), url('/ChatGPT Image Sep 27, 2025, 09_35_27 AM.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        // Prevent mobile zoom
        fontSize: '16px'
      }}
    >
      {/* Global Top Bar - burger (mobile) + signup CTA */}
      <div className="fixed top-0 inset-x-0 z-[60] bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border h-16">
        <div className="mx-auto max-w-6xl px-3 sm:px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Burger - mobile only */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="inline-flex lg:hidden h-9 w-9 items-center justify-center rounded-md hover:bg-muted/60 border border-border/40"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                title="Menu"
              >
                <span className="sr-only">Open menu</span>
                {/* Simple burger icon */}
                <div className="flex flex-col gap-1">
                  <span className="block h-0.5 w-5 bg-foreground/70" />
                  <span className="block h-0.5 w-5 bg-foreground/70" />
                  <span className="block h-0.5 w-5 bg-foreground/70" />
                </div>
              </button>
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">Signed in as</div>
                <div className="text-sm font-medium">{user.email}</div>
              </div>
            ) : (
              <Button size="sm" onClick={onSignupClick} className="h-8 px-4 ml-auto text-sm">
                Create account
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Left: onboarding sidebar */}
      <div className="w-[240px] hidden lg:block border rounded-lg p-3 bg-background shadow-sm ml-4 mt-24 h-fit">
        <OnboardingSidebarChat highlightCalendar={drawerOpen} onOpenCalendarDrawer={() => setDrawerOpen(true)} stepsOverride={displaySteps} />
      </div>

      {/* Right: chat */}
      <div className="flex-1 flex flex-col max-w-2xl mr-4 lg:mr-4 mr-0 mt-24">
        {/* Mobile Analysis Progress hidden; accessible via burger menu */}

        {/* Assistant header - desktop only */}
        <div className="hidden lg:block px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">🌿</div>
            <div>
              <div className="tracking-tight text-lg font-medium">Content garden assistant</div>
              <div className="text-sm text-muted-foreground">Let's grow something amazing together</div>
            </div>
          </div>
        </div>

        <ScrollArea ref={scrollRef as any} className="flex-1 px-4">
          <div className="space-y-4 pb-4">
            {messages.map((m, idx) => {
              // Render idea cards when we detect the marker content
              try {
                const parsed = JSON.parse(m.content);
                if ((parsed?._type === 'ideas' && Array.isArray(parsed.items)) || 
                    (parsed?.ideas && Array.isArray(parsed.ideas))) {
                  return (
                    <div key={idx} className="flex justify-start">
                      <div className="relative">
                        {/* Magic sparkles background */}
                        <div className="absolute -inset-4 opacity-0 animate-sparkle-reveal">
                          <div className="absolute top-0 left-0 text-yellow-400 animate-sparkle" style={{ animationDelay: '0s' }}>✨</div>
                          <div className="absolute top-2 right-2 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.3s' }}>⭐</div>
                          <div className="absolute bottom-1 left-3 text-yellow-400 animate-sparkle" style={{ animationDelay: '0.6s' }}>✨</div>
                          <div className="absolute bottom-0 right-0 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.9s' }}>⭐</div>
                        </div>
                        {/* Card with magical entrance */}
                        <div className="relative w-full max-w-2xl animate-magic-reveal">
                          <IdeaCards 
                            ideas={parsed.items || parsed.ideas} 
                            onCreateOutline={handleCreateOutline}
                          />
                        </div>
                      </div>
                    </div>
                  );
                } else if ((parsed?._type === 'post_draft' || parsed?.post) && parsed.post) {
                  return (
                    <div key={idx} className="flex justify-start">
                      <div className="relative">
                        {/* Magic sparkles background */}
                        <div className="absolute -inset-4 opacity-0 animate-sparkle-reveal">
                          <div className="absolute top-0 left-0 text-yellow-400 animate-sparkle" style={{ animationDelay: '0s' }}>✨</div>
                          <div className="absolute top-2 right-2 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.3s' }}>⭐</div>
                          <div className="absolute bottom-1 left-3 text-yellow-400 animate-sparkle" style={{ animationDelay: '0.6s' }}>✨</div>
                          <div className="absolute bottom-0 right-0 text-yellow-300 animate-sparkle" style={{ animationDelay: '0.9s' }}>⭐</div>
                        </div>
                        {/* Card with magical entrance */}
                        <div className="relative w-full max-w-2xl animate-magic-reveal">
                          <PostDraftCard
                            title={parsed.post.title}
                            intro={parsed.post.digest || ''}
                            buttonLabel={(calendarItems.find((it) => (it.title || '').trim().toLowerCase() === (parsed.post.title || '').trim().toLowerCase()) ? 'Go to post' : undefined)}
                            onAddPlanned={async () => {
                              console.log('OnboardingChatDesktop: Write about this topic button clicked!', {
                                title: parsed.post.title,
                                currentWebsite: currentWebsite,
                                calendarItemsCount: calendarItems.length
                              });
                              
                              // Check if user is authenticated
                              const { data: sessionData } = await supabase.auth.getSession();
                              if (!sessionData?.session) {
                                console.log('OnboardingChatDesktop: User not authenticated, showing signup dialog');
                                // Store the post data for creation after signup
                                localStorage.setItem('pendingPostCreation', JSON.stringify({
                                  title: parsed.post.title,
                                  digest: parsed.post.digest
                                }));
                                // Use the existing onSignupClick handler
                                onSignupClick();
                                return;
                              }

                              // User is authenticated, proceed with post creation
                              console.log('OnboardingChatDesktop: User authenticated, proceeding with post creation');
                              
                              // Store in localStorage for access after redirect
                              localStorage.setItem('pendingPostCreation', JSON.stringify({
                                title: parsed.post.title,
                                digest: parsed.post.digest
                              }));

                              // Redirect to main app dashboard where the post will be created
                              window.location.href = '/dashboard';
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                }
              } catch {}

              // Render regular chat messages
              return (
                <div
                  key={idx}
                  className={`flex ${m.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`max-w-xl rounded-2xl px-4 py-3 text-sm shadow-sm transition-all duration-300 ${
                    m.role === 'assistant' 
                      ? 'bg-background border border-brand-green-border text-left' 
                      : 'bg-background text-foreground border border-transparent text-right'
                  }`}>
                    {m.role === 'assistant' ? (
                      <div className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-li:text-foreground">
                        {m._loading ? (
                          <div className="flex items-center space-x-2">
                            <div className="relative w-6 h-6">
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-4 h-4 text-brand-green-border animate-grow-leaf">
                                  🌱
                                </div>
                              </div>
                            </div>
                            <span className="text-muted-foreground text-sm">
                              {m._mode === 'idea' ? 'Generating ideas...' : 
                               m._mode === 'write' ? 'Creating outline...' : 
                               ''}
                            </span>
                          </div>
                        ) : m._streaming && m._typing ? (
                          <div className="flex items-center space-x-2">
                            <span>{m.content}</span>
                            <div className="w-2 h-2 bg-brand-green-border rounded-full animate-pulse"></div>
                          </div>
                        ) : (
                          <ReactMarkdown 
                            components={{
                              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                              ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                              li: ({ children }) => <li className="text-sm">{children}</li>,
                              strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                              a: ({ href, children }) => {
                                if (href === 'signup') {
                                  return (
                                    <button 
                                      onClick={onSignupClick}
                                      className="text-primary hover:text-primary/80 underline cursor-pointer bg-transparent border-none p-0 font-inherit"
                                    >
                                      {children}
                                    </button>
                                  );
                                }
                                return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline">{children}</a>;
                              }
                            }}
                          >
                            {m.content}
                          </ReactMarkdown>
                        )}
                      </div>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        </ScrollArea>

        {/* Chat input - sticky on mobile */}
        <div className="p-4 lg:bg-transparent">
          <div className="flex items-center gap-2 rounded-full border border-brand-green-border bg-muted/30 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-brand-green-border/50">
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted transition-colors"
              aria-label="Add"
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
            </button>

            <Input
              ref={inputRef}
              placeholder="What's on your mind?"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { 
                if (e.key === 'Enter' && !e.shiftKey) { 
                  e.preventDefault(); 
                  onSend(); 
                } 
              }}
              className="flex-1 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-base"
            />

            <button
              type="button"
              className="hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted transition-colors"
              aria-label="Voice"
            >
              <Mic className="h-4 w-4 text-muted-foreground" />
            </button>

            <Button
              onClick={onSend}
              disabled={sending || !input.trim()}
              className="h-9 w-9 rounded-full p-0 shadow-sm"
              title="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Calendar Drawer */}
      <CalendarDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={calendarItems}
        onRemove={(id) => {
          setCalendarItems((prev) => {
            const next = prev.filter((i) => i.id !== id);
            localStorage.setItem('calendar_items', JSON.stringify(next));
            return next;
          });
        }}
        onEdit={(item) => {
          console.log('Editing item:', {
            id: item.id,
            title: item.title,
            isGenerated: item.isGenerated,
            hasFullContent: !!item.fullContent,
            fullContentLength: item.fullContent?.length || 0
          });
          
          // Always open drawer for all items
          setWriteDrawerData({
            id: item.id, // Pass the item ID for regeneration
            subject_matter: item.title,
            post_content: item.fullContent || '', // Pass the generated content or empty string
            post_digest: item.digest || '' // Pass the digest for the card display
          });
          setWriteDrawerOpen(true);
          setDrawerOpen(false);
        }}
      />

      {/* Mobile Sidebar Drawer: Website settings & navigation */}
      <AppDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        width="md"
        position="left"
      >
        <div className="h-full flex flex-col">
          <div className="p-3 border-b">
            <div className="text-sm font-medium">Website settings</div>
          </div>
          <div className="flex-1 overflow-auto p-3">
            <OnboardingSidebarChat
              highlightCalendar={drawerOpen}
              onOpenCalendarDrawer={() => {
                setMobileMenuOpen(false);
                setDrawerOpen(true);
              }}
              stepsOverride={displaySteps}
            />
          </div>
        </div>
      </AppDrawer>

      {/* Write Post Drawer */}
      <WritePostDrawer
        isOpen={writeDrawerOpen}
        onClose={() => {
          setWriteDrawerOpen(false);
          setWriteDrawerData(null);
        }}
        onBack={() => {
          setWriteDrawerOpen(false);
          setDrawerOpen(true); // Go back to calendar drawer
        }}
        subject_matter={writeDrawerData?.subject_matter || ''}
        post_content={writeDrawerData?.post_content || ''}
        post_digest={writeDrawerData?.post_digest}
        onArticleGenerated={(article) => {
          // Store the generated content for persistence
          console.log('Article generated:', article);
          setWriteDrawerData(prev => prev ? { ...prev, post_content: article.post_content } : null);
          
          // Check if we're editing an existing item (has an ID in writeDrawerData)
          if (writeDrawerData?.id) {
            // Update existing item
            setCalendarItems((prev) => {
              const updated = prev.map(item => 
                item.id === writeDrawerData.id 
                  ? { 
                      ...item, 
                      title: article.subject_matter,
                      digest: generateDigest(article.post_content),
                      fullContent: article.post_content
                    }
                  : item
              );
              localStorage.setItem('calendar_items', JSON.stringify(updated));
              console.log('Updated existing article:', updated);
              return updated;
            });
          } else {
            // Create new item
            const item: CalendarItem = { 
              id: crypto.randomUUID(), 
              title: article.subject_matter, 
              digest: generateDigest(article.post_content),
              fullContent: article.post_content,
              isGenerated: true
            };
            setCalendarItems((prev) => {
              const next = [item, ...prev];
              localStorage.setItem('calendar_items', JSON.stringify(next));
              localStorage.setItem('calendar_enabled', '1');
              window.dispatchEvent(new Event('calendar-enabled-changed'));
              console.log('Added generated article to calendar:', next);
              return next;
            });
          }
        }}
      />

      {/* Signup Dialog */}
      <SignupDialog />
    </div>
  );
}